import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Stepper,
    Step,
    StepLabel,
    Box,
    Typography,
    Button,
    ToggleButton,
    ToggleButtonGroup,
    FormControl,
    Select,
    MenuItem,
    Stack,
    InputLabel,
    FormControlLabel,
    Checkbox,
    Alert,
    Tooltip,
} from '@mui/material';
import RequestService from '../../services/RequestService';
import { ReactComponent as CoinIcon } from '../../assets/icons/coin.svg';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const ContentDialog = ({ open, onClose, onSuccess, content, user }) => {
    const aiAvailable = Boolean(user?.companyPlan?.aiAutoReviewEnabled);
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        type: 'MISSION',
        attitudeType: '',
        subType: '',
        difficultyLevel: 'EASY',
        exp: 0,
        coin: 0,
        startDate: '',
        endDate: '',
        completionLimit: '',
        recurrenceTime: '',
        isLimitedTime: false,
        isAutomatic: false,
        allowTextProof: false,
        allowFileProof: false,
        locationRestricted: false,
        isEntireGame: false,
        games: [],
        groups: [],
        aiCorePoints: '',
    });

    const [approvalMode, setApprovalMode] = useState('CONVENTIONAL');
    const [groups, setGroups] = useState([]);
    const [games, setGames] = useState([]);
    const [isEntireGame, setIsEntireGame] = useState(
        content?.games?.length > 0
    );

    const steps = ['Informações', 'Detalhes', 'Validação', 'Revisão'];

    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const data = await RequestService.getGroups(user.company.id);
                setGroups(Array.isArray(data) ? data : []);
            } catch (error) {
                setGroups([]);
            }
        };

        const fetchGames = async () => {
            try {
                const gamesData = await RequestService.getGames(
                    user.company.id
                );
                setGames(Array.isArray(gamesData) ? gamesData : []);
            } catch (error) {
                setGames([]);
            }
        };

        if (content) {
            const convertToDateTimeLocal = (arr) => {
                if (!arr || arr.length < 5) return '';

                const [year, month, day, hour, minute] = arr;
                const dateObj = new Date(year, month - 1, day, hour, minute);

                const pad = (n) => n.toString().padStart(2, '0');

                return `${dateObj.getFullYear()}-${pad(dateObj.getMonth() + 1)}-${pad(dateObj.getDate())}T${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}`;
            };

            setFormData({
                title: content.title || '',
                description: content.description || '',
                type: content.type || 'MISSION',
                attitudeType: content.attitudeType || '',
                subType: content.subType || '',
                difficultyLevel: content.difficultyLevel || 'EASY',
                exp: content.exp || 0,
                coin: content.coin || 0,
                isLimitedTime: content.isLimitedTime ?? false,
                completionLimit: content.completionLimit ?? null,
                startDate: convertToDateTimeLocal(content.startDate),
                endDate: convertToDateTimeLocal(content.endDate),
                recurrenceTime: content.recurrenceTime
                    ? `${String(content.recurrenceTime[0]).padStart(2, '0')}:${String(content.recurrenceTime[1]).padStart(2, '0')}`
                    : '',
                isAutomatic: content.isAutomatic === true,
                allowTextProof: content.allowTextProof ?? false,
                allowFileProof: content.allowFileProof ?? false,
                locationRestricted: content.isAutomatic
                    ? 'VALIDATION_REQUIRED'
                    : '',
                isEntireGame: content.isEntireGame ?? false,
                groups: content.groups
                    ? content.groups.map((group) => group.id)
                    : [],
                games: content.games
                    ? content.games.map((game) => game.id)
                    : [],
                aiCorePoints: content.aiCorePoints || '',
            });

            if (aiAvailable && content.iaEnabled) {
                setApprovalMode('AI');
            } else {
                setApprovalMode('CONVENTIONAL');
            }

            setIsEntireGame(content.games && content.games.length > 0);
        }

        fetchGames();
        fetchGroups();
    }, [content, user.company.id]);

    const handleApprovalToggle = (event, value) => {
        if (value !== null) {
            setApprovalMode(value);
        }
    };

    const handleNext = () => {
        setActiveStep((prevStep) => {
            if (
                prevStep === 1 &&
                formData.type === 'ATTITUDE' &&
                formData.attitudeType === 'NEGATIVE'
            ) {
                return prevStep + 2;
            }
            return prevStep + 1;
        });
    };

    const handleBack = () => {
        setActiveStep((prevStep) => {
            if (
                prevStep === 3 &&
                formData.type === 'ATTITUDE' &&
                formData.attitudeType === 'NEGATIVE'
            ) {
                return prevStep - 2;
            }
            return prevStep - 1;
        });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]:
                type === 'checkbox'
                    ? checked
                    : type === 'number'
                      ? Number(value)
                      : value,
        }));
    };

    const handleToggleChange = (name) => (event, value) => {
        if (value !== null) {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const handleSubmit = async () => {
        try {
            const payload = {
                title: formData.title,
                description: formData.description,
                type: formData.type,
                difficultyLevel: formData.difficultyLevel,
                exp: formData.exp,
                coin: formData.coin,
                isAutomatic: formData.isAutomatic,
                allowTextProof: formData.allowTextProof,
                allowFileProof: formData.allowFileProof,
                isLimitedTime: content.isLimitedTime,
                startDate: formData.startDate,
                endDate: formData.endDate,
                location: formData.isAutomatic ? 'VALIDATION_REQUIRED' : '',
                companyId: user.company.id,
                subType: formData.subType,
            };

            if (isEntireGame) {
                payload.isEntireGame = true;
                payload.gameIds = formData.games;
            } else {
                payload.isEntireGame = false;
                payload.groupIds = formData.groups;
            }

            if (
                formData.type === 'ATTITUDE' &&
                formData.attitudeType === 'NEGATIVE'
            ) {
                payload.attitudeType = 'NEGATIVE';
                payload.subType = 'NORMAL';
                payload.isLimitedTime = false;
                payload.validationMethod = 'MANUAL';
            } else if (formData.type === 'ATTITUDE') {
                payload.attitudeType = 'POSITIVE';
            }

            if (
                formData.subType === 'LIMITED' &&
                formData.completionLimit > 0
            ) {
                payload.completionLimit = formData.completionLimit;
            } else {
                payload.completionLimit = null;
            }

            if (aiAvailable && approvalMode === 'AI') {
                payload.iaEnabled = true;
                payload.aiCorePoints = formData.aiCorePoints || '';
            } else {
                payload.iaEnabled = false;
            }

            await RequestService.updateContent(content.id, payload);

            setFormData({
                title: '',
                description: '',
                type: 'MISSION',
                subType: '',
                attitudeType: '',
                completionLimit: 0,
                difficulty: 'EASY',
                repetition: 'ONCE',
                customRepetition: '',
                startDate: '',
                endDate: '',
                isLimitedTime: false,
                isEntireGame: false,
                games: [],
                groups: [],
                exp: 0,
                coin: 0,
                validationMethod: 'MANUAL',
                locationRestricted: false,
            });

            setActiveStep(0);

            onSuccess('Content updated successfully!');
            onClose();
        } catch (err) {
            console.error('Failed to update content', err);
        }
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <TextField
                            label="Título"
                            name="title"
                            value={formData.title}
                            onChange={handleChange}
                            fullWidth
                        />
                        <TextField
                            label="Descrição"
                            name="description"
                            value={formData.description}
                            onChange={(e) => {
                                if (e.target.value.length <= 300) {
                                    setFormData((prev) => ({
                                        ...prev,
                                        description: e.target.value,
                                    }));
                                }
                            }}
                            fullWidth
                            multiline
                            rows={3}
                            helperText={`${formData.description.length}/300`}
                            FormHelperTextProps={{
                                sx: {
                                    color:
                                        formData.description.length === 300
                                            ? 'red'
                                            : 'gray',
                                },
                            }}
                        />
                        <Typography variant="body1">Formato</Typography>
                        <ToggleButtonGroup
                            value={formData.type}
                            fullWidth
                            exclusive
                            onChange={handleToggleChange('type')}
                            color="primary"
                        >
                            <ToggleButton value="MISSION">Missão</ToggleButton>
                            <ToggleButton value="ATTITUDE">
                                Atitude
                            </ToggleButton>
                        </ToggleButtonGroup>
                        {formData.type === 'ATTITUDE' && (
                            <>
                                <Typography variant="body1">
                                    Essa atitude é uma atitude positiva ou
                                    negativa?
                                </Typography>
                                <ToggleButtonGroup
                                    value={formData.attitudeType}
                                    exclusive
                                    fullWidth
                                    onChange={handleToggleChange(
                                        'attitudeType'
                                    )}
                                    color="primary"
                                >
                                    <ToggleButton value="POSITIVE">
                                        Positiva
                                    </ToggleButton>
                                    <ToggleButton value="NEGATIVE">
                                        Negativa
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </>
                        )}
                        <Typography variant="body1">Dificuldade</Typography>
                        <ToggleButtonGroup
                            value={formData.difficultyLevel}
                            exclusive
                            fullWidth
                            onChange={handleToggleChange('difficultyLevel')}
                            color="primary"
                        >
                            <ToggleButton value="EASY">Fácil</ToggleButton>
                            <ToggleButton value="INTERMEDIATE">
                                Normal
                            </ToggleButton>
                            <ToggleButton value="HARD">Difícil</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                );
            case 1:
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        {formData.type !== 'ATTITUDE' &&
                            formData.attitudeType !== 'POSITIVE' && (
                                <>
                                    <ToggleButtonGroup
                                        value={formData.subType}
                                        fullWidth
                                        exclusive
                                        onChange={handleToggleChange('subType')}
                                        color="primary"
                                    >
                                        <ToggleButton value="UNIQUE">
                                            Uma vez
                                        </ToggleButton>
                                        <ToggleButton value="LIMITED">
                                            Personalizado
                                        </ToggleButton>
                                        <ToggleButton value="NORMAL">
                                            Sem Limite
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                    {formData.subType === 'LIMITED' && (
                                        <TextField
                                            label="Insira a quantidade"
                                            name="completionLimit"
                                            type="number"
                                            value={formData.completionLimit}
                                            onChange={handleChange}
                                            fullWidth
                                        />
                                    )}

                                    <Typography variant="body1">
                                        Data de validade
                                    </Typography>
                                    <ToggleButtonGroup
                                        value={formData.isLimitedTime}
                                        fullWidth
                                        exclusive
                                        onChange={handleToggleChange(
                                            'isLimitedTime'
                                        )}
                                        color="primary"
                                    >
                                        <ToggleButton value={false}>
                                            Sem validade
                                        </ToggleButton>
                                        <ToggleButton value={true}>
                                            Selecionar dia
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                    {formData.isLimitedTime === true && (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 2,
                                            }}
                                        >
                                            <TextField
                                                helperText="Data Inicial"
                                                name="startDate"
                                                type="datetime-local"
                                                value={formData.startDate}
                                                onChange={handleChange}
                                                fullWidth
                                            />
                                            <TextField
                                                helperText="Data Final"
                                                name="endDate"
                                                type="datetime-local"
                                                value={formData.endDate}
                                                onChange={handleChange}
                                                fullWidth
                                            />
                                        </Box>
                                    )}
                                </>
                            )}
                        <Typography variant="body1">
                            Esse conteúdo será disponibilizado para:
                        </Typography>
                        <ToggleButtonGroup
                            value={isEntireGame}
                            fullWidth
                            exclusive
                            onChange={(event, newValue) => {
                                if (newValue !== null) {
                                    setIsEntireGame(newValue);
                                }
                            }}
                            color="primary"
                        >
                            <ToggleButton value={true}>
                                Jogos Selecionados
                            </ToggleButton>
                            <ToggleButton value={false}>
                                Grupos específicos
                            </ToggleButton>
                        </ToggleButtonGroup>
                        {/* IF ATTITUDE make the user choice if:
                        It will be applied to all Selected Game's groups or just specific Groups */}
                        {isEntireGame === true && (
                            <FormControl>
                                <InputLabel>Jogos</InputLabel>
                                <Select
                                    name="games"
                                    value={formData.games}
                                    onChange={handleChange}
                                    multiple
                                >
                                    {(games || []).map((game) => (
                                        <MenuItem key={game.id} value={game.id}>
                                            {game.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        {isEntireGame === false && (
                            <FormControl>
                                <InputLabel>Grupos</InputLabel>
                                <Select
                                    name="groups"
                                    value={formData.groups}
                                    onChange={handleChange}
                                    multiple
                                >
                                    {(groups || []).map((group) => (
                                        <MenuItem
                                            key={group.id}
                                            value={group.id}
                                        >
                                            {group.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                        <Stack direction="row" justifyContent="space-between">
                            <Stack direction="row" gap={1}>
                                <CoinIcon />
                                <TextField
                                    label="Recompensa (Moedas)"
                                    name="coin"
                                    type="number"
                                    value={formData.coin}
                                    onChange={handleChange}
                                    fullWidth
                                />
                            </Stack>
                        </Stack>
                    </Box>
                );
            case 2:
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        {aiAvailable && (
                            <FormControl>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                    sx={{ my: 1 }}
                                >
                                    <Typography class="body1">
                                        Aprovação
                                    </Typography>
                                    <Tooltip title="A aprovação convencional exigirá a aprovação manual de cada solicitação de conclusão. A Aprovação com a Gio utilizará a Gio, nossa Inteligência Artificial, para validar automaticamente as solicitações. Caso ela não consiga validar, ela solicitará aprovação manual.">
                                        <HelpOutlineIcon
                                            color="primary"
                                            sx={{
                                                width: '18px',
                                                height: '18px',
                                            }}
                                        />
                                    </Tooltip>
                                </Stack>
                                <ToggleButtonGroup
                                    value={approvalMode}
                                    fullWidth
                                    exclusive
                                    onChange={handleApprovalToggle}
                                    color="primary"
                                >
                                    <ToggleButton value="CONVENTIONAL">
                                        Convencional
                                    </ToggleButton>
                                    <ToggleButton value="AI">
                                        Validação com a Gio
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </FormControl>
                        )}

                        {aiAvailable && approvalMode === 'AI' ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                }}
                            >
                                <TextField
                                    label="aiCorePoints"
                                    name="aiCorePoints"
                                    value={formData.aiCorePoints}
                                    onChange={handleChange}
                                    fullWidth
                                    helperText="Parâmetros/briefing para a aprovação por IA."
                                />
                                {content?.iaEnabled &&
                                    !formData.aiCorePoints && (
                                        <Alert severity="info">
                                            Este conteúdo já utilizava IA.
                                            Revise os pontos de avaliação se
                                            necessário.
                                        </Alert>
                                    )}
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 2,
                                }}
                            >
                                <FormControl>
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        gap={1}
                                        sx={{ my: 1 }}
                                    >
                                        <Typography variant="body1">
                                            Método de Validação
                                        </Typography>
                                        <Tooltip title="O método de validação automático irá aprovar automaticamente todas as solicitações de conclusão desta missão desde que o jogador esteja, pessoalmente, nas dependências da escola.">
                                            <HelpOutlineIcon
                                                color="primary"
                                                sx={{
                                                    width: '18px',
                                                    height: '18px',
                                                }}
                                            />
                                        </Tooltip>
                                    </Stack>

                                    <ToggleButtonGroup
                                        value={formData.isAutomatic}
                                        fullWidth
                                        exclusive
                                        onChange={(event, value) => {
                                            if (value !== null) {
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    isAutomatic: value === true,
                                                }));
                                            }
                                        }}
                                        color="primary"
                                    >
                                        <ToggleButton value={false}>
                                            Manual
                                        </ToggleButton>
                                        <ToggleButton value={true}>
                                            Automático
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                </FormControl>
                            </Box>
                        )}
                        {!formData.isAutomatic && (
                            <Box>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="allowTextProof"
                                            checked={
                                                formData.allowTextProof || false
                                            }
                                            onChange={handleChange}
                                        />
                                    }
                                    label="Obrigatória comprovação por texto"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="allowFileProof"
                                            checked={
                                                formData.allowFileProof || false
                                            }
                                            onChange={handleChange}
                                        />
                                    }
                                    label="Obrigatória comprovação através de envio de arquivo"
                                />
                            </Box>
                        )}
                    </Box>
                );
            case 3:
                return (
                    <Box>
                        <Typography variant="body2">
                            <strong>Título:</strong> {formData.title}
                        </Typography>
                        <Typography variant="body2">
                            <strong>Descrição:</strong> {formData.description}
                        </Typography>
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Editar Conteúdo</DialogTitle>
            <DialogContent>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                {renderStepContent(activeStep)}
            </DialogContent>
            <DialogActions>
                {activeStep > 0 && (
                    <Button onClick={handleBack} variant="outlined">
                        Voltar
                    </Button>
                )}
                {activeStep < steps.length - 1 ? (
                    <Button onClick={handleNext} variant="contained">
                        Próximo
                    </Button>
                ) : (
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                    >
                        Salvar Alterações
                    </Button>
                )}
                <Button onClick={onClose} variant="outlined">
                    Cancelar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ContentDialog;
